import React, { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import type { ReportComponentProps } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { GET_CMA_BROKER_REVIEWS_PAGINATED } from '@modules/reports/cma-reports/cmaReportsQueries';
import { ClientReviewCard } from '@modules/reports/cma-reports/step-agent/ClientReviewCard';
import { splitLoadedPage } from '@modules/reports/cma-reports/step-agent/loadMorePage';
import type { BrokerReview } from '@modules/reports/cma-reports/step-agent/types';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import type { CMAPageId } from '@modules/reports/shared/utils';
import { useLocale } from '@shared/hooks/locale';

const INITIAL_LOAD_LIMIT = 15;
const PAGINATED_LOAD_LIMIT = 8;

const StepClientTestimonials = ({ setStep, step }: ReportComponentProps) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [lastPageHasMore, setLastPageHasMore] = useState<boolean | null>(null);
  const [pendingReviewId, setPendingReviewId] = useState<string | null>(null);
  const { data: cmaReport } = useReportContext<CMAPageId>();

  const brokerId = cmaReport?.broker_id;

  const visibleReviewIds = useMemo(
    () =>
      'cma_user_reviews' in cmaReport
        ? cmaReport.cma_user_reviews.map(r => r.review_id)
        : [],
    [cmaReport],
  );

  const {
    data: paginatedReviewsData,
    loading: paginatedReviewsLoading,
    fetchMore,
  } = useQuery(GET_CMA_BROKER_REVIEWS_PAGINATED, {
    variables: {
      brokerId: brokerId ?? '',
      limit: INITIAL_LOAD_LIMIT + 1,
      offset: 0,
    },
    skip: !brokerId,
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const { items: reviews, hasMore } = splitLoadedPage(
    paginatedReviewsData?.reviews ?? [],
    lastPageHasMore,
    INITIAL_LOAD_LIMIT,
  );

  const handleLoadMore = async () => {
    if (!brokerId || paginatedReviewsLoading || !hasMore) {
      return;
    }

    const nextOffset = reviews.length;

    await fetchMore({
      variables: {
        offset: nextOffset,
        limit: PAGINATED_LOAD_LIMIT + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const newReviews = fetchMoreResult.reviews;
        setLastPageHasMore(newReviews.length > PAGINATED_LOAD_LIMIT);

        return {
          reviews: [
            ...(prev.reviews ?? []).slice(0, nextOffset),
            ...newReviews,
          ],
        };
      },
    });
  };

  if (paginatedReviewsLoading && reviews.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Stack
        sx={{
          gap: 2,
          p: 2,
          flexGrow: 1,
          height: 0,
          overflow: 'auto',
        }}
      >
        {reviews.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('No reviews found')}
            </Typography>
          </Box>
        )}

        {reviews.length > 0 && (
          <>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {reviews.map(review => {
                const reviewWithVisibility: BrokerReview = {
                  ...review,
                  is_visible: visibleReviewIds.includes(review.id),
                };

                return (
                  <ClientReviewCard
                    key={review.id}
                    review={reviewWithVisibility}
                    locked={pendingReviewId != null}
                    onPendingChange={setPendingReviewId}
                  />
                );
              })}
            </Stack>

            {hasMore && (
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                loadingPosition="start"
                loading={paginatedReviewsLoading}
                fullWidth
              >
                {t('Load more')}
              </Button>
            )}
          </>
        )}
      </Stack>
      <FooterActionsComponentWithoutForm
        updating={paginatedReviewsLoading}
        step={step}
        setStep={setStep}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => {
          setErrorLike(null);
        }}
      />
    </Box>
  );
};

export default StepClientTestimonials;
